import { useEffect } from "react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import "../../styles/home.css";
import "../../styles/how-prufyy-works.css";
import { initScrollReveal } from "../../utils/scrollReveal";
import { initScrollProgress } from "../../utils/scrollProgress";

function Incubator() {
  useEffect(() => {
    initScrollReveal();
    initScrollProgress();
  }, []);

  return (
    <>
      <Header />

      {/* SCROLL PROGRESS */}
      <div className="scroll-progress">
        <div className="scroll-progress-fill" />
      </div>

      <main className="hpw-wrapper">
        {/* ================= HERO ================= */}
        <section className="hero-section reveal">
          <div className="hero-content">
            <h1>
              Incubation Is a Credibility Responsibility <br />
              Not Just Founder Support
            </h1>
            <p>
              Incubators do more than nurture startups. They implicitly endorse
              credibility, shape evaluator expectations, and influence how
              early-stage ventures are trusted beyond the program.
            </p>
          </div>
        </section>

        {/* ================= AUDIENCE ================= */}
        <section className="section reveal">
          <span className="audience-badge">Incubator Perspective</span>
          <h2>For Incubators Managing Long-Term Signal Quality</h2>
          <p>
            Every cohort compounds your reputation. Inconsistent evaluation,
            narrative-heavy applications, and undocumented decisions quietly
            erode institutional trust over time.
          </p>
        </section>

        {/* ================= PROBLEM ================= */}
        <section className="section reveal">
          <h2>Where Incubators Struggle Quietly</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Inconsistent Evaluation</h4>
              <p>Startup evaluations vary across mentors and programs.</p>
            </div>

            <div className="info-card">
              <h4>Narrative-Heavy Intake</h4>
              <p>Applications rely heavily on storytelling.</p>
            </div>

            <div className="info-card">
              <h4>Weak Defensibility</h4>
              <p>Selection decisions are hard to justify later.</p>
            </div>

            <div className="info-card">
              <h4>Lost Learnings</h4>
              <p>Cohort insights disappear after each cycle.</p>
            </div>

            <div className="info-card">
              <h4>People-Dependent Memory</h4>
              <p>Institutional memory lives only in individuals.</p>
            </div>
          </div>
        </section>

        {/* ================= NEED ================= */}
        <section className="section reveal">
          <h2>What Incubators Actually Need</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Structured Intake</h4>
              <p>Structured intake without suppressing founders.</p>
            </div>

            <div className="info-card">
              <h4>Claim Visibility</h4>
              <p>Claim-level visibility across startups.</p>
            </div>

            <div className="info-card">
              <h4>Independent Reviews</h4>
              <p>Mentor evaluations without peer influence.</p>
            </div>

            <div className="info-card">
              <h4>Defensible Decisions</h4>
              <p>Selection logic that holds up over time.</p>
            </div>

            <div className="info-card">
              <h4>Cohort Memory</h4>
              <p>Long-term credibility memory across cohorts.</p>
            </div>
          </div>
        </section>

        {/* ================= STRUCTURE ================= */}
        <section className="section reveal">
          <h2>Introducing Structure at the Cohort Level</h2>

          <div className="flow-diagram">
            <div className="flow-step">
              <div className="flow-icon">📥</div>
              <h3>Startup Intake</h3>
              <p>Founder narratives enter the program.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">🧩</div>
              <h3>Claims Structured</h3>
              <p>Key claims are isolated consistently.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">📎</div>
              <h3>Evidence Attached</h3>
              <p>Supporting material lives with each claim.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">👥</div>
              <h3>Independent Review</h3>
              <p>Mentors evaluate without influence.</p>
            </div>
          </div>
        </section>

        {/* ================= EXAMPLE ================= */}
        <section className="section reveal">
          <h2>Example: Cohort Intake with Clarity</h2>

          <p>
            Two startups may claim similar traction. Prufyy reveals where
            credibility actually differs — not through scoring, but through
            evidence quality and evaluator interpretation.
          </p>

          <div className="claim-evidence">
            <div className="claim-box">
              <strong>Claim</strong>
              <p>“We have validated market demand.”</p>
            </div>

            <div className="link-line" />

            <div className="evidence-box">
              <strong>Evidence</strong>
              <ul>
                <li>Pilot customer data</li>
                <li>User interviews</li>
                <li>Early adoption metrics</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ================= EVALUATION ================= */}
        <section className="section reveal">
          <h2>Evaluation Without Scoring or Bias</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>No Rankings</h4>
              <p>Startups are never ordered or scored.</p>
            </div>

            <div className="info-card">
              <h4>No Forced Comparison</h4>
              <p>Evaluations remain independent.</p>
            </div>

            <div className="info-card">
              <h4>Private Drafts</h4>
              <p>Mentors review without visibility into others.</p>
            </div>

            <div className="info-card">
              <h4>Locked Submissions</h4>
              <p>Reviews become accountable records.</p>
            </div>

            <div className="info-card">
              <h4>No Pressure</h4>
              <p>Accountability without performative judgment.</p>
            </div>
          </div>
        </section>

        {/* ================= INSTITUTIONAL MEMORY ================= */}
        <section className="section reveal">
          <h2>Institutional Memory That Compounds</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Reviewable History</h4>
              <p>Past cohorts remain accessible.</p>
            </div>

            <div className="info-card">
              <h4>Emerging Patterns</h4>
              <p>Credibility trends appear naturally.</p>
            </div>

            <div className="info-card">
              <h4>Defensible Logic</h4>
              <p>Selection reasoning persists over time.</p>
            </div>

            <div className="info-card">
              <h4>Persistent Learning</h4>
              <p>Knowledge survives beyond individuals.</p>
            </div>
          </div>
        </section>

        {/* ================= REPUTATION ================= */}
        <section className="section reveal">
          <h2>Protecting the Incubator’s Reputation</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>No Public Discovery</h4>
              <p>Startups are never exposed publicly.</p>
            </div>

            <div className="info-card">
              <h4>No Implicit Endorsement</h4>
              <p>Support does not equal validation.</p>
            </div>

            <div className="info-card">
              <h4>Revocable Access</h4>
              <p>Sharing can be withdrawn anytime.</p>
            </div>

            <div className="info-card">
              <h4>Clear Boundaries</h4>
              <p>Support and evaluation remain distinct.</p>
            </div>
          </div>
        </section>

        {/* ================= FINAL CTA ================= */}
        <section className="final-cta-section reveal">
          <h2>Bring Structure to Your Cohort Evaluations</h2>
          <p>
            Prufyy helps incubators support founders responsibly while preserving
            credibility and institutional trust.
          </p>
          <a href="/signup" className="btn-primary">
            Get Started with Prufyy
          </a>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default Incubator;
